import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod/v4";
import { useFieldArray } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import debounce from "lodash.debounce";
import ReactJson from "react-json-view";
import FormSelect from "../shared/FormSelect";
import FormInputText from "../shared/FormInputText";
import { FaRegTrashCan } from "react-icons/fa6";
import { fetchAddUsersToGroup, fetchAddTagsToGroup, fetchRemoveUsersFromGroup, fetchRemoveTagsFromGroup, fetchTagsInGroup, fetchUsersInGroup } from "../../utils/apiUtils";
import { loadTags } from "../../utils/optionLoaderUtils";
import { useInView } from "react-intersection-observer";
import { useInfiniteQuery, useMutation, } from "@tanstack/react-query";
import SharedTable from "../benefitPlan/SharedTable";
import { queryClient } from "../../App";


const validDomains = ['hotmail.com', 'centivo.com'];
const domainPattern = new RegExp(`@(${validDomains.map(d => d.replace('.', '\\.')).join('|')})$`, 'i');

const emailSchemaRegex = z.email()
    .regex(domainPattern, "Email must be from an approved domain");



const GroupDetails = ({ queryKey, group, autoRefreshEnabled }) => {
    const [activeSwitch, setActiveSwitch] = useState('USERS');
    return (
        <div style={{ width: '100%', padding: '1rem' }}>
            <p className="title">Group Detail</p>
            <p className="sub-title">Collection of users that has tags that will be used to send alerts</p>

            <div className="switch" style={{ width: 'fit-content', margin: '1rem 0rem' }}>
                <p
                    className={`option ${activeSwitch === "USERS" ? "active" : ""}`}
                    onClick={() => setActiveSwitch("USERS")}>
                    Users
                </p>
                <p
                    className={`option ${activeSwitch === "TAGS" ? "active" : ""}`}
                    onClick={() => setActiveSwitch("TAGS")}>
                    Tags
                </p>
            </div>

            {activeSwitch === "USERS" ? <Users
                groupsQueryKey={queryKey}
                group={group}
                autoRefreshEnabled={autoRefreshEnabled}
                queryKey
            /> : activeSwitch === 'TAGS' ? <Tags groupsQueryKey={queryKey}
                group={group}
                queryKey
            /> : <></>}

        </div>
    )
}

export default GroupDetails

const Users = ({ group, groupsQueryKey, autoRefreshEnabled }) => {
    const [searchValue, setSearchValue] = useState();
    const [decouncedSearchValue, setDecouncedSearchValue] = useState();

    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState(false)

    const { ref, inView } = useInView();
    const queryKey = ['job-registry-table-group', group, decouncedSearchValue]

    const {
        data,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        isLoading, //first time only
        isFetching,
        hasNextPage,
        isFetched,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isError,
        status,
        error
    } = useInfiniteQuery({
        retry: false,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: true,
        // placeholderData: (prev) => prev,
        queryKey,
        queryFn: ({ pageParam }) => {
            let params = { lastEvaluatedKey: pageParam, groupPk: group.pk, email: decouncedSearchValue };

            return fetchUsersInGroup(params)
        },
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.LastEvaluatedKey ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey)) : null
        },
        select: (data) => {
            return {
                pages: data.pages
                    .map((page) => page.Items?.map(item => {
                        return item
                    })).flatMap((x) => x),
                pageParams: data.pageParams,
            };
        },
        staleTime: 0,
        enabled: true
    })

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);


    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim()?.toLowerCase());
        }, 600),
        []
    );

    const deletehandler = async ({ groupPk, groupSk, users }) => {
        return await fetchRemoveUsersFromGroup({
            groupPk,
            groupSk,
            users
        })
    }

    const addHandler = async ({ groupPk, groupSk, users }) => {

        const result = await fetchAddUsersToGroup({
            groupPk,
            groupSk,
            users
        })
        queryClient.invalidateQueries([groupsQueryKey])
        return result
    }


    const deleteMutation = useMutation({
        mutationFn: deletehandler,
        onSuccess: (data, variables) => {

            if (!data) return

            queryClient.setQueryData(queryKey, (oldData => {
                if (oldData) {
                    for (let i = 0; i < oldData.pages.length; i++) {
                        let page = oldData.pages[i];
                        page.Items = page.Items.filter((item) => !variables.users.some(u => u.email === item.email));
                        oldData.pages[i] = page;
                    }
                }
                return oldData
            }));
            queryClient.invalidateQueries([groupsQueryKey])
        },
        onError: (err) => {
            console.log(err)
            // setError(err?.response?.data?.message || err.message)
        }
    })


    const items = data?.pages.flat();
    let columns = ['pk', 'sk', 'email'];

    return <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 0rem 1rem 0rem', }}>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>
                <div>
                    <label style={{ display: 'block', paddingBottom: '.6rem' }}>Search by Email or Enter a valid email to add.</label>
                    <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder={'Email...'}
                        autoComplete="off"
                        onChange={(e) => {
                            setErrorMessage('')
                            setSearchValue(e.target.value?.toLowerCase());
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />

                </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyItems: 'center', alignItems: 'start' }}>

                <div>
                    {emailSchemaRegex.safeParse(searchValue).success && !items?.length && !isFetching && !isLoading && !loading ?


                        <div className={"outline-btn"}
                            onClick={async () => {
                                try {
                                    setLoading(true)
                                    await addHandler({
                                        groupPk: group.pk, groupSk: group.sk, users: [{ email: searchValue }]
                                    })
                                    await refetch()

                                } catch (error) {
                                    setErrorMessage(error.message)
                                } finally {
                                    setLoading(false)
                                }

                            }}
                        >{'Add User'}</div> : loading ? <div className="loader" /> :
                            <></>}
                    {errorMessage && <p className="danger" >{errorMessage}</p>}
                </div>



            </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
            <p
                style={{ cursor: "pointer", color: "var(--red)" }}
                onClick={() => {

                    setDecouncedSearchValue("");
                    setSearchValue("");
                }}
            >
                Clear filters
            </p>

        </div>


        {status === "loading" ? (
            <p>Loading...</p>
        ) : status === "error" ? (
            <span className='danger'>Error: {error.message}</span>
        ) : (
            <>
                <SharedTable
                    type={"job-registry-group-user"}
                    columns={columns}
                    items={items}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    autoRefreshEnabled={autoRefreshEnabled}
                    onDeleteHandler={async (user) => {
                        await deleteMutation.mutateAsync({
                            groupPk: user.groupPk,
                            groupSk: user.groupSk,
                            users: [user]
                        })

                    }}
                    // onDetailHandler={onDetailHandler}
                    ref={ref}
                />
            </>
        )}

    </div>
}

const Tags = ({ group, groupsQueryKey, autoRefreshEnabled }) => {
    const [searchValue, setSearchValue] = useState();
    const [decouncedSearchValue, setDecouncedSearchValue] = useState();

    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState(false)

    const [selectedTags, setSelectedTags] = useState(null)
    const [cacheUniq, setCacheUniq] = useState(new Date().getTime())


    const { ref, inView } = useInView();
    const queryKey = ['job-registry-table-tag', group, decouncedSearchValue]

    const {
        data,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        isLoading, //first time only
        isFetching,
        hasNextPage,
        isFetched,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isError,
        status,
        error
    } = useInfiniteQuery({
        retry: false,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: true,
        // placeholderData: (prev) => prev,
        queryKey,
        queryFn: ({ pageParam }) => {

            let params = { lastEvaluatedKey: pageParam, groupPk: group.pk, tag: decouncedSearchValue };

            return fetchTagsInGroup(params)
        },
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.LastEvaluatedKey ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey)) : null
        },
        select: (data) => {
            return {
                pages: data.pages
                    .map((page) => page.Items?.map(item => {
                        return item
                    })).flatMap((x) => x),
                pageParams: data.pageParams,
            };
        },
        staleTime: 0,
        enabled: true
    })

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim()?.toLowerCase());
        }, 600),
        []
    );

    const deletehandler = async ({ groupPk, groupSk, tags }) => {
        return await fetchRemoveTagsFromGroup({
            groupPk,
            groupSk,
            tags
        })
    }

    const addHandler = async ({ groupPk, groupSk, tags }) => {

        const result = await fetchAddTagsToGroup({
            groupPk,
            groupSk,
            tags
        })

        queryClient.invalidateQueries([groupsQueryKey])
        setCacheUniq(new Date().getTime())
        return result
    }

    const deleteMutation = useMutation({
        mutationFn: deletehandler,
        onSuccess: (data, variables) => {

            if (!data) return

            queryClient.setQueryData(queryKey, (oldData => {
                if (oldData) {
                    for (let i = 0; i < oldData.pages.length; i++) {
                        let page = oldData.pages[i];
                        page.Items = page.Items.filter((item) => !variables.tags.some(t => t.sk === item.sk));
                        oldData.pages[i] = page;
                    }
                }
                return oldData
            }));

            setCacheUniq(new Date().getTime())

        },
        onError: (err) => {
            console.log(err)
            // setError(err?.response?.data?.message || err.message)
        }
    })

    const items = data?.pages.flat();
    let columns = ['pk', 'sk', 'tag'];

    return <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 0rem 1rem 0rem', }}>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>


                <div>
                    <label style={{ display: 'block', paddingBottom: '.6rem' }}>Search by Tag</label>
                    <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder={'Tag...'}
                        autoComplete="off"
                        onChange={(e) => {
                            setErrorMessage('')
                            setSearchValue(e.target.value?.toLowerCase());
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />

                </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyItems: 'center', alignItems: 'end' }}>

                <div>
                    <label style={{ display: 'block', paddingBottom: '0.6rem' }}>Add Tags</label>
                    <AsyncPaginate
                        placeholder={"Select a tag to add..."}
                        className="react-select-container large-input"
                        classNamePrefix={"react-select"}
                        debounceTimeout={300}
                        name={'tags'}
                        cacheUniqs={[cacheUniq]}
                        value={selectedTags}
                        onChange={async (val) => {
                            setSelectedTags(val)
                        }}
                        isMulti={true}
                        loadOptions={(search, loadedOptions, additional) => loadTags(search, { ...loadedOptions, filter: items.map(i => i.tag) }, additional)}
                    />
                </div>

                <div>
                    {selectedTags?.length ?

                        <div className={"outline-btn"}
                            style={{ lineHeight: '30px', height: '49px', }}
                            onClick={async () => {
                                try {
                                    setLoading(true)

                                    if (selectedTags?.length) {
                                        await addHandler({
                                            groupPk: group.pk, groupSk: group.sk, tags: selectedTags?.map(({ value }) => ({ tag: value }))
                                        })
                                    }

                                    setSelectedTags([])


                                } catch (error) {
                                    setErrorMessage(error.message)
                                } finally {
                                    setLoading(false)
                                }
                                await refetch()
                            }}
                        >{'Add Tag'}</div> : loading ? <div className="loader" /> :
                            <></>}
                    {errorMessage && <p className="danger" >{errorMessage}</p>}
                </div>



            </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
            <p
                style={{ cursor: "pointer", color: "var(--red)" }}
                onClick={() => {

                    setDecouncedSearchValue("");
                    setSearchValue("");
                }}
            >
                Clear filters
            </p>

        </div>


        {status === "loading" ? (
            <p>Loading...</p>
        ) : status === "error" ? (
            <span className='danger'>Error: {error.message}</span>
        ) : (
            <>
                <SharedTable
                    type={"job-registry-group-tag"}
                    columns={columns}
                    items={items}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    autoRefreshEnabled={autoRefreshEnabled}
                    onDeleteHandler={async (tag) => {
                        await deleteMutation.mutateAsync({
                            groupPk: tag.groupPk,
                            groupSk: tag.groupSk,
                            tags: [tag]
                        })

                    }}
                    // onDetailHandler={onDetailHandler}
                    ref={ref}
                />
            </>
        )}

    </div>
}